import { gql, useMutation, useQuery } from "@apollo/client";

// ── GraphQL Documents ─────────────────────────────────────

export const GET_PRACTICE_PROFILE = gql`
  query PracticeProfile {
    currentTenant {
      id
      phone
      email
      addresses
      socialLinks
    }
  }
`;

export const UPDATE_PRACTICE_PROFILE = gql`
  mutation UpdatePracticeProfile($input: TenantConfigInput!) {
    updateTenantConfig(input: $input) {
      id
      phone
      email
      addresses
      socialLinks
    }
  }
`;

// ── TypeScript types ─────────────────────────────────────

export interface TenantAddress {
  label?: string;
  street?: string;
  city?: string;
  state?: string;
  zip?: string;
}

export interface TenantSocialLink {
  platform: string;
  url: string;
}

interface PracticeProfileTenant {
  id: string;
  phone: string | null;
  email: string | null;
  /** JSON string of TenantAddress[] (private API serializes JSON columns as strings). */
  addresses: string | null;
  /** JSON string of TenantSocialLink[]. */
  socialLinks: string | null;
}

export interface PracticeProfileData {
  currentTenant: PracticeProfileTenant | null;
}

interface UpdatePracticeProfileVariables {
  input: {
    phone?: string;
    email?: string;
    /** JSON-encoded TenantAddress[]. */
    addresses?: string;
    /** JSON-encoded TenantSocialLink[]. */
    socialLinks?: string;
  };
}

interface UpdatePracticeProfileData {
  updateTenantConfig: PracticeProfileTenant | null;
}

// ── Parse helpers ─────────────────────────────────────

function parseJsonArray<T>(raw: string | null | undefined): T[] {
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw) as unknown;
    return Array.isArray(parsed) ? (parsed as T[]) : [];
  } catch {
    return [];
  }
}

export function parseAddresses(raw: string | null | undefined): TenantAddress[] {
  return parseJsonArray<TenantAddress>(raw);
}

export function parseSocialLinks(
  raw: string | null | undefined,
): TenantSocialLink[] {
  return parseJsonArray<TenantSocialLink>(raw);
}

// ── React hooks ─────────────────────────────────────

export function usePracticeProfileQuery() {
  return useQuery<PracticeProfileData>(GET_PRACTICE_PROFILE);
}

export function useUpdatePracticeProfileMutation() {
  // The mutation returns the Tenant with `id`, so Apollo normalizes the
  // result into the cache and every currentTenant consumer re-renders.
  return useMutation<UpdatePracticeProfileData, UpdatePracticeProfileVariables>(
    UPDATE_PRACTICE_PROFILE,
  );
}
